import { motion } from "framer-motion";
import { MapPin, Package, Truck, ShieldCheck, Clock, TrendingUp } from "lucide-react";

const highlights = [
  { icon: MapPin, label: "Basée à Dakar, Sénégal" },
  { icon: Package, label: "4 marques distribuées" },
  { icon: Truck, label: "Distribution nationale" },
  { icon: ShieldCheck, label: "Qualité certifiée" },
];

const features = [
  {
    icon: MapPin,
    title: "Couverture Régionale",
    desc: "Réseau de distribution couvrant le Sénégal, Mali, Guinée, Gambie et Mauritanie.",
  },
  {
    icon: Clock,
    title: "Livraison Rapide",
    desc: "Délais optimisés grâce à une flotte moderne et des entrepôts stratégiquement positionnés.",
  },
  {
    icon: Package,
    title: "Chaîne du Froid",
    desc: "Infrastructure frigorifique complète pour garantir la fraîcheur de chaque produit.",
  },
  {
    icon: TrendingUp,
    title: "Traçabilité Totale",
    desc: "Suivi numérique complet de chaque lot, de l'import à la livraison finale.",
  },
];

const CompanySection = () => (
  <section id="about" className="section-padding bg-card relative overflow-x-clip overflow-y-visible">
    {/* Anchor for logistics deep-link */}
    <div id="logistics" className="absolute top-0" />

    {/* Ambient blobs */}
    <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-secondary/5 blur-3xl" />
    <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-accent/5 blur-3xl" />

    <div className="container-max px-5 sm:px-8 lg:px-12 relative">
      <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 xl:gap-24">
        {/* Left: About */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-block px-3 py-1 rounded-md bg-secondary/10 text-secondary font-semibold text-xs uppercase tracking-[0.15em] mb-5">
            À Propos de CIEDA
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-6 text-balance font-display">
            Votre partenaire de confiance en{" "}
            <span className="text-gradient-gold">distribution africaine</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4 text-base">
            CIEDA — Compagnie Import Export & Distribution Africaine — gère le parcours complet des
            produits de consommation, de l'importation jusqu'à la mise en rayon.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8 text-base">
            Basée à Hann Mariste II, Dakar, nous assurons qualité, fiabilité et rapidité à chaque
            étape de la chaîne d'approvisionnement à travers le Sénégal.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {highlights.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-background rounded-xl p-5 border border-border group hover:border-secondary/30 transition-all duration-300 cursor-default"
                style={{ boxShadow: "var(--card-shadow)" }}
              >
                <item.icon
                  className="mb-3 text-secondary group-hover:text-gold-dark transition-colors"
                  size={24}
                />
                <div className="text-sm font-semibold text-foreground">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right: Logistics / Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-block px-3 py-1 rounded-md bg-secondary/10 text-secondary font-semibold text-xs uppercase tracking-[0.15em] mb-5">
            Logistique & Distribution
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-8 text-balance font-display">
            Un réseau de distribution{" "}
            <span className="text-gradient-gold">fiable</span>
          </h2>

          <div className="space-y-5">
            {features.map((feat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex gap-4 group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/5 border border-primary/10 flex items-center justify-center group-hover:bg-secondary/10 group-hover:border-secondary/20 transition-all duration-300">
                  <feat.icon
                    className="text-primary group-hover:text-secondary transition-colors"
                    size={20}
                  />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1 text-sm">{feat.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feat.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default CompanySection;
